import { c as _c } from "react/compiler-runtime";
import type { MutableRefObject } from "react";
import DropsList from "@/components/drops/view/DropsList";
import { WaveDropsReverseContainer } from "@/components/waves/drops/WaveDropsReverseContainer";
import { WaveDropsScrollBottomButton } from "@/components/waves/drops/WaveDropsScrollBottomButton";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { useVirtualizedWaveDrops } from "@/hooks/useVirtualizedWaveDrops";
type WaveMessagesResult = ReturnType<typeof useVirtualizedWaveDrops>["waveMessages"];
interface WaveDropsMessageListSectionProps {
  readonly waveMessages: WaveMessagesResult;
  readonly dropId: string | null;
  readonly scrollContainerRef: MutableRefObject<HTMLDivElement | null>;
  readonly bottomAnchorRef: MutableRefObject<HTMLDivElement | null>;
  readonly onTopIntersection: () => void;
  readonly onReply: ({
    drop,
    partId
  }: {
    drop: ApiDrop;
    partId: number;
  }) => void;
  readonly onQuote: ({
    drop,
    partId
  }: {
    drop: ApiDrop;
    partId: number;
  }) => void;
  readonly queueSerialTarget: (serialNo: number) => void;
  readonly activeDrop: ActiveDropState | null;
  readonly serialTarget: number | null;
  readonly targetDropRef: MutableRefObject<HTMLDivElement | null>;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly isAtBottom: boolean;
  readonly scrollToBottom: () => void;
  readonly onDropContentClick?: (drop: ExtendedDrop) => void;
  readonly pendingCount: number;
  readonly onRevealPending: () => void;
}
const MIN_DROPS_FOR_PAGINATION = 25;
export const WaveDropsMessageListSection: React.FC<WaveDropsMessageListSectionProps> = t0 => {
  const $ = _c(32);
  const {
    waveMessages,
    dropId,
    scrollContainerRef,
    bottomAnchorRef,
    onTopIntersection,
    onReply,
    onQuote,
    queueSerialTarget,
    activeDrop,
    serialTarget,
    targetDropRef,
    onQuoteClick,
    isAtBottom,
    scrollToBottom,
    onDropContentClick,
    pendingCount,
    onRevealPending
  } = t0;
  const hasNextPage = !!waveMessages?.hasNextPage && (waveMessages?.drops?.length ?? 0) >= MIN_DROPS_FOR_PAGINATION;
  const t1 = !!waveMessages?.isLoadingNextPage;
  let t2;
  if ($[0] !== waveMessages?.drops) {
    t2 = waveMessages?.drops ?? [];
    $[0] = waveMessages?.drops;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== activeDrop || $[3] !== dropId || $[4] !== onDropContentClick || $[5] !== onQuote || $[6] !== onQuoteClick || $[7] !== onReply || $[8] !== queueSerialTarget || $[9] !== scrollContainerRef || $[10] !== serialTarget || $[11] !== t2 || $[12] !== targetDropRef) {
    t3 = <DropsList scrollContainerRef={scrollContainerRef} onReplyClick={queueSerialTarget} drops={t2} showWaveInfo={false} onReply={onReply} onQuote={onQuote} showReplyAndQuote={true} activeDrop={activeDrop} serialNo={serialTarget} targetDropRef={targetDropRef} onQuoteClick={onQuoteClick} parentContainerRef={scrollContainerRef} dropViewDropId={dropId} onDropContentClick={onDropContentClick} key="drops-list" />;
    $[2] = activeDrop;
    $[3] = dropId;
    $[4] = onDropContentClick;
    $[5] = onQuote;
    $[6] = onQuoteClick;
    $[7] = onReply;
    $[8] = queueSerialTarget;
    $[9] = scrollContainerRef;
    $[10] = serialTarget;
    $[11] = t2;
    $[12] = targetDropRef;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      height: "1px"
    };
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== bottomAnchorRef) {
    t5 = <div ref={bottomAnchorRef} style={t4} />;
    $[15] = bottomAnchorRef;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== hasNextPage || $[18] !== onTopIntersection || $[19] !== scrollContainerRef || $[20] !== t1 || $[21] !== t3 || $[22] !== t5) {
    t6 = <WaveDropsReverseContainer ref={scrollContainerRef} isFetchingNextPage={t1} hasNextPage={hasNextPage} onTopIntersection={onTopIntersection}>{t3}{t5}</WaveDropsReverseContainer>;
    $[17] = hasNextPage;
    $[18] = onTopIntersection;
    $[19] = scrollContainerRef;
    $[20] = t1;
    $[21] = t3;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== isAtBottom || $[25] !== onRevealPending || $[26] !== pendingCount || $[27] !== scrollToBottom) {
    t7 = <WaveDropsScrollBottomButton isAtBottom={isAtBottom} scrollToBottom={scrollToBottom} newMessagesCount={pendingCount} onRevealNewMessages={onRevealPending} />;
    $[24] = isAtBottom;
    $[25] = onRevealPending;
    $[26] = pendingCount;
    $[27] = scrollToBottom;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] !== t6 || $[30] !== t7) {
    t8 = <>{t6}{t7}</>;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  return t8;
};